import {
  WBBox,
  WBCheckbox,
  WBFlex,
  WBTextFieldV2,
  WBToggleButtonGroup,
  WBTypography,
  WBRoundedToggleButton,
} from '@admiin-com/ds-web';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AutoCompleteLookupV2 from '../../AutoCompleteLookup/AutoCompleteLookupV2';
import React from 'react';
import { isValidPRN } from '@admiin-com/ds-common';
import { Bill } from '../PayBillCreateForm';
import { isEntityOrContact } from '../../../helpers/entities';
import {
  DocumentAnalysisLineItemInput,
  PaymentFrequency,
  PayoutMethod,
  TaxType,
} from '@admiin-com/ds-graphql';
import { InputLabel, styled, useMediaQuery, useTheme } from '@mui/material';
import { BillLineItems } from '../BillLineItems';
import { LineItemsMobile } from './Scanning/Mobile/LineItemsMobile';
import { useDefaultValueForFromTo } from '../../../features/providers/TaskCreationProvider/TaskCreationProvider';
import {
  getNestedError,
  sanitizeReferenceNumber,
} from '../../../helpers/string';
import { getAmountFromLineItems } from '../../../helpers/lineitems';
import { TASK_FORM_FIELDS } from '../../../helpers/form';
import DueDateSelectorV2 from '../../DueDateSelector/DueDateSelectorV2';
import { getContactCreateInputFromBill } from '../useBillCreate/helpers';
import { ScanFeedback } from '../../ScanFeedback/ScanFeedback';
interface Props {
  bill: Bill;
  index?: number | undefined | null;
  noBank?: boolean;
  isManualBillInput?: boolean;
}

export const BillForm = ({
  bill,
  index,
  noBank = false,
  isManualBillInput,
}: Props) => {
  const { t } = useTranslation();
  const fieldName = index === undefined ? `` : `bills.${index}.`;
  const {
    control,
    watch,
    setValue,
    formState: { isDirty, errors },
  } = useFormContext();
  const entity = useDefaultValueForFromTo();
  console.log('entity: ', entity);

  const from = watch(`${fieldName}from`);
  const isTax = React.useMemo(
    () => (from as any)?.metadata?.subCategory === 'TAX',
    [from]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isBpay =
    Boolean(watch(`${fieldName}bpay`)) &&
    Boolean(watch(`${fieldName}bpay.billerCode`)) &&
    !!bill?.documentAnalysis;

  const bpayReferenceNumber = useWatch({
    control,
    name: `${fieldName}bpay.referenceNumber`,
  });

  const hasBank = Boolean(watch(`${fieldName}bank`));

  const isbankEmpty = !hasBank;

  const billerCode =
    from?.bpay?.billerCode || from?.billerCode || from?.metadata?.billerCode;

  const isFromBpay =
    (!isTax && (from as any)?.metadata?.payoutMethod === PayoutMethod.BPAY) ||
    !!billerCode;
  const inputs = React.useMemo(
    () => ({
      invoiceId: {
        ...TASK_FORM_FIELDS.reference,
        label: t('invoiceId', { ns: 'taskbox' }),
        name: `${fieldName}reference`,
        type: 'text',
        placeholder: t('invoiceIdPlaceholder', { ns: 'taskbox' }),
      },
      saveBpayRefForFuture: {
        label: t('saveBpayRefForFuture', { ns: 'taskbox' }),
        name: `${fieldName}saveBpayRefForFuture`,
        type: 'checkbox',
        defaultValue: true,
        rules: {},
      },
      from: {
        label: t('from', { ns: 'taskbox' }),
        name: `${fieldName}from`,
        defaultValue: null,
        placeholder: t('atoOptional', { ns: 'taskbox' }),
        rules: { required: t('fromRequired', { ns: 'taskbox' }) },
      },
      to: {
        label: t('to', { ns: 'taskbox' }),
        name: `${fieldName}to`,
        placeholder: t('entitySearchPlaceholder', { ns: 'taskbox' }),
        defaultValue: entity,
        rules: { required: t('toRequired', { ns: 'taskbox' }) },
      },
      dueAt: {
        ...TASK_FORM_FIELDS.dueAt,
        name: `${fieldName}dueAt`,
      },
      issuedAt: {
        ...TASK_FORM_FIELDS.issuedAt,
        name: `${fieldName}issuedAt`,
      },

      routingNumber: {
        label: t('bsb', { ns: 'contacts' }),
        name: `${fieldName}bank.routingNumber`,
        type: 'text',
        placeholder: t('bsbPlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: !isbankEmpty
          ? {
              required: t('bsbRequired', { ns: 'contacts' }),
              pattern: {
                value: /^\d{6}$/,
                message: t('bsbPattern', { ns: 'contacts' }),
              },
            }
          : {},
      },
      accountNumber: {
        label: t('accountNumber', { ns: 'contacts' }),
        name: `${fieldName}bank.accountNumber`,
        type: 'text',
        placeholder: t('accountNumberPlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: !isbankEmpty
          ? {
              required: t('accountNumberRequired', { ns: 'contacts' }),
              minLength: {
                value: 6,
                message: t('accountNumberMinLength', {
                  ns: 'contacts',
                  count: 6,
                }),
              },
              maxLength: {
                value: 12,
                message: t('accountNumberMaxLength', {
                  ns: 'contacts',
                  count: 12,
                }),
              },
            }
          : {},
      },
      bpay: {
        billerCode: {
          label: t('billerCode', { ns: 'contacts' }),
          name: `${fieldName}bpay.billerCode`,
          type: 'number',
          placeholder: t('billerCodePlaceholder', { ns: 'contacts' }),
          defaultValue: '',
          rules: {
            validate: {
              required: (value: number | undefined) => {
                if (bpayReferenceNumber && !value) {
                  return t('billerCodeRequired', { ns: 'contacts' });
                }
                return true;
              },
              format: (value: number | undefined) => {
                if (value && !/^\d+$/.test(String(value))) {
                  return t('billerCodeMustBeNumbers', { ns: 'contacts' });
                }
                return true;
              },
            },
          },
        },

        referenceNumber: {
          label: isTax
            ? t('paymentReferenceNumber', { ns: 'taskbox' })
            : t('bpayReferenceNumber', { ns: 'contacts' }),
          name: `${fieldName}bpay.referenceNumber`,
          type: 'text',
          placeholder: isTax
            ? t('paymentReferenceNumberPlaceholder', { ns: 'taskbox' })
            : t('bpayReferenceNumber', { ns: 'contacts' }),
          defaultValue: '',
          rules: {
            validate: {
              isATOorBpay: (value: string | undefined) => {
                if (!value && isTax) {
                  return t('prnRequired', { ns: 'taskbox' });
                }
                if (isTax && value && !isValidPRN(value)) {
                  return t('prnInvalid', { ns: 'taskbox' });
                }
                if (!value) {
                  return t('bpayReferenceRequired', { ns: 'taskbox' });
                }
                if (value && !/^\d{2,20}$/.test(value)) {
                  return t('bpayReferenceInvalid', { ns: 'taskbox' });
                }
                return true;
              },
            },
          },
        },
      },
    }),
    [t, isTax, entity, isbankEmpty, bpayReferenceNumber, fieldName]
  );

  const isfromNotHasBank =
    !!from &&
    isEntityOrContact(from) === 'CONTACT' &&
    (!from.bank?.accountNumber || !from.bank?.routingNumber);

  const isFromHasBank =
    !!from &&
    isEntityOrContact(from) === 'CONTACT' &&
    !!from.bank?.accountNumber &&
    !!from.bank?.routingNumber;

  const entityId = watch(`${fieldName}to.id`);

  const paymentFrequency = useWatch({
    control,
    name: `${fieldName}paymentFrequency`,
  });

  React.useEffect(() => {
    if (!isBpay)
      if (isFromBpay) {
        if (!watch(`${fieldName}bpay.billerCode`)) {
          setValue(`${fieldName}bpay.billerCode`, billerCode);
        }
        if (!watch(`${fieldName}bpay.referenceNumber`)) {
          setValue(
            `${fieldName}bpay.referenceNumber`,
            from.bpay?.referenceNumber ?? null
          );
        }
      } else if (!isFromBpay) {
        console.log('reset bpay', isFromBpay);
        setValue(`${fieldName}bpay`, null);
      }
  }, [isFromBpay, isBpay, fieldName]);

  const changeBankField = () => {
    console.log('changing bank', isfromNotHasBank, from?.bank, index);
    if (isFromHasBank) {
      setValue(`${fieldName}bank`, from?.bank, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else if (isDirty && !bill?.documentAnalysis?.bank) {
      setValue(
        `${fieldName}bank`,
        { accountNumber: '', routingNumber: '' },
        { shouldValidate: true, shouldDirty: true }
      );
    }
  };

  React.useEffect(() => {
    changeBankField();
  }, [isFromHasBank, isDirty]);

  React.useEffect(() => {
    if (isTax) {
      const lineItems = watch(`${fieldName}lineItems`);
      const newLineItems = lineItems.map((item: any) => {
        if (item.taxType === TaxType.GST_INCLUSIVE) {
          return {
            ...item,
            taxType: 'NO_GST',
          };
        }
        return item;
      });
      setValue(`${fieldName}lineItems`, newLineItems);
    }
  }, [isTax]);

  const updateLineItems = (lineItems: DocumentAnalysisLineItemInput[]) => {
    setValue(`${fieldName}lineItems`, lineItems);
    setValue(`${fieldName}amount`, getAmountFromLineItems(lineItems));
  };

  return (
    <>
      <WBFlex flexDirection={['column', 'row']} gap={[0, 4]}>
        <Controller
          control={control}
          name={inputs.invoiceId.name}
          rules={inputs.invoiceId.rules}
          defaultValue={inputs.invoiceId.defaultValue}
          render={({ field, formState: { errors } }) => (
            <WBTextFieldV2
              {...field}
              type={inputs.invoiceId.type}
              placeholder={inputs.invoiceId.placeholder}
              label={inputs.invoiceId.label}
              key={field.name}
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) ?? ''
              }
              fullWidth
            />
          )}
        />
        <Controller
          control={control}
          name={inputs.from.name}
          rules={inputs.from.rules}
          defaultValue={inputs.from.defaultValue}
          render={({ field, formState: { errors } }) => (
            <AutoCompleteLookupV2
              {...field}
              noPopupIcon
              label={inputs.from.label}
              placeholder={inputs.from.placeholder}
              ocrCreate
              entityId={entityId}
              type={'ContactsAndVerifiedEntity'}
              helperText={
                (getNestedError(errors, field.name)?.message as string) ?? ''
              }
              error={!!getNestedError(errors, field.name)}
              creatingContact={getContactCreateInputFromBill(bill)}
            />
          )}
        />
      </WBFlex>
      <Controller
        control={control}
        name={inputs.to.name}
        rules={inputs.to.rules}
        defaultValue={inputs.to.defaultValue}
        render={({ field, formState: { errors } }) => <div />}
      />
      <WBFlex flexDirection={['column', 'row']} gap={[0, 4]}>
        <Controller
          control={control}
          name={inputs.issuedAt.name}
          rules={inputs.issuedAt.rules}
          defaultValue={inputs.issuedAt.defaultValue}
          render={({ field, formState: { errors } }) => (
            <DueDateSelectorV2
              {...field}
              type={inputs.issuedAt.type}
              placeholder={inputs.issuedAt.placeholder}
              label={inputs.issuedAt.label}
              fullWidth
              helperText={
                (getNestedError(errors, field.name)?.message as string) ?? ''
              }
              error={!!getNestedError(errors, field.name)}
            />
          )}
        />
        <Controller
          control={control}
          name={inputs.dueAt.name}
          rules={inputs.dueAt.rules}
          defaultValue={inputs.dueAt.defaultValue}
          render={({ field, formState: { errors } }) => (
            <DueDateSelectorV2
              {...field}
              type={inputs.dueAt.type}
              placeholder={inputs.dueAt.placeholder}
              label={inputs.dueAt.label}
              fullWidth
              helperText={
                (getNestedError(errors, field.name)?.message as string) ?? ''
              }
              error={!!getNestedError(errors, field.name)}
            />
          )}
        />
      </WBFlex>
      {isBpay || isTax || isFromBpay ? (
        <>
          <WBFlex flexDirection={['column', 'row']} gap={[0, 4]}>
            <Controller
              control={control}
              name={inputs.bpay.referenceNumber.name}
              rules={inputs.bpay.referenceNumber.rules}
              defaultValue={inputs.bpay.referenceNumber.defaultValue}
              render={({ field, formState: { errors } }) => (
                <WBTextFieldV2
                  {...field}
                  type={inputs.bpay.referenceNumber.type}
                  placeholder={inputs.bpay.referenceNumber.placeholder}
                  label={inputs.bpay.referenceNumber.label}
                  key={field.name}
                  fullWidth
                  error={!!getNestedError(errors, field.name)}
                  helperText={
                    (getNestedError(errors, field.name)?.message as string) ??
                    ''
                  }
                  onChange={(e) => {
                    const sanitized = sanitizeReferenceNumber(e.target.value);
                    field.onChange(sanitized);
                  }}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    // Strip spaces from pasted text
                    const strippedText = sanitizeReferenceNumber(pastedText);
                    field.onChange(strippedText);
                  }}
                />
              )}
            />
          </WBFlex>
          {(isBpay || isFromBpay) && (
            <Controller
              control={control}
              name={inputs.saveBpayRefForFuture.name}
              rules={inputs.saveBpayRefForFuture.rules}
              defaultValue={inputs.saveBpayRefForFuture.defaultValue}
              render={({ field, formState: { errors } }) => (
                <WBCheckbox
                  {...field}
                  checked={field.value ?? false}
                  label={inputs.saveBpayRefForFuture.label}
                  key={field.name}
                />
              )}
            />
          )}
        </>
      ) : (
        // ((hasMissingBankField && !!bill.documentAnalysis) ||
        //   isfromNotHasBank) &&
        !!from &&
        !noBank && (
          <WBFlex flexDirection={['column', 'row']} gap={[0, 4]}>
            <Controller
              control={control}
              name={inputs.routingNumber.name}
              rules={inputs.routingNumber.rules}
              defaultValue={inputs.routingNumber.defaultValue}
              render={({ field, formState: { errors } }) => (
                <WBTextFieldV2
                  {...field}
                  type={inputs.routingNumber.type}
                  placeholder={inputs.routingNumber.placeholder}
                  label={inputs.routingNumber.label}
                  key={field.name}
                  error={!!getNestedError(errors, field.name)}
                  helperText={
                    (getNestedError(errors, field.name)?.message as string) ??
                    ''
                  }
                  fullWidth
                />
              )}
            />
            <Controller
              control={control}
              name={inputs.accountNumber.name}
              rules={inputs.accountNumber.rules}
              defaultValue={inputs.accountNumber.defaultValue}
              render={({ field, formState: { errors } }) => (
                <WBTextFieldV2
                  {...field}
                  type={inputs.accountNumber.type}
                  placeholder={inputs.accountNumber.placeholder}
                  label={inputs.accountNumber.label}
                  key={field.name}
                  fullWidth
                  error={!!getNestedError(errors, field.name)}
                  helperText={
                    (getNestedError(errors, field.name)?.message as string) ??
                    ''
                  }
                />
              )}
            />
          </WBFlex>
        )
      )}
      <WBBox mt={2}>
        <Controller
          name={`${fieldName}paymentFrequency`}
          control={control}
          defaultValue={PaymentFrequency.ONCE}
          render={({ field }) => (
            <WBToggleButtonGroup
              {...field}
              size="medium"
              fullWidth={isMobile ? true : false}
              exclusive
              sx={{ marginTop: 2, gap: 1 }}
              label={t('paymentFrequency', { ns: 'taskbox' })}
            >
              {[PaymentFrequency.ONCE, PaymentFrequency.MONTHLY].map(
                (key: PaymentFrequency) => {
                  return (
                    <WBRoundedToggleButton
                      value={key}
                      key={key}
                      selected={
                        key === PaymentFrequency.ONCE
                          ? key === paymentFrequency
                          : paymentFrequency !== 'ONCE'
                      }
                    >
                      {t(key === PaymentFrequency.ONCE ? key : 'RECURRING', {
                        ns: 'taskbox',
                      })}
                    </WBRoundedToggleButton>
                  );
                }
              )}
            </WBToggleButtonGroup>
          )}
        />
      </WBBox>

      {paymentFrequency !== PaymentFrequency.ONCE && (
        <WBBox mt={2}>
          <Controller
            name={`${fieldName}paymentFrequency`}
            control={control}
            defaultValue={PaymentFrequency.ONCE}
            render={({ field }) => (
              <WBToggleButtonGroup
                {...field}
                size="medium"
                fullWidth={isMobile ? true : false}
                exclusive
                sx={{
                  marginTop: 2,
                  gap: 1,
                  display: 'flex',
                  padding: 0,
                  flexWrap: 'wrap',
                }}
              >
                {Object.keys(PaymentFrequency)
                  .filter((key) => key !== PaymentFrequency.ONCE)
                  .map((key) => {
                    return (
                      <WBRoundedToggleButton
                        value={key}
                        key={key}
                        size="small"
                        sx={{
                          padding: 1,
                          fontSize: theme.typography.body2.fontSize,
                        }}
                      >
                        {t(key, {
                          ns: 'taskbox',
                        })}
                      </WBRoundedToggleButton>
                    );
                  })}
              </WBToggleButtonGroup>
            )}
          />
        </WBBox>
      )}
      <Controller
        control={control}
        name={`${fieldName}amount`}
        defaultValue={0}
        rules={{
          validate: (value) =>
            value > 0 || t('amountMustBeGreaterThanZero', { ns: 'taskbox' }), // Ensures value is > 0
        }}
        render={({ field, formState: { errors } }) => (
          <>
            {!isMobile && index !== undefined && (
              <LineItemsContainer>
                <WBTypography
                  component={'span'}
                  sx={{ opacity: 0.3 }}
                  fontSize={'inherit'}
                  fontWeight={'bold'}
                >
                  {t('lineItems', { ns: 'taskbox' })}
                </WBTypography>
                <BillLineItems
                  lineItems={bill?.lineItems ?? []}
                  setLineItems={updateLineItems}
                />
              </LineItemsContainer>
            )}
            {isMobile && isManualBillInput && (
              <WBBox mt={2}>
                <InputLabel>{t('lineItems', { ns: 'taskbox' })}</InputLabel>
                <LineItemsMobile
                  lineItems={bill?.lineItems ?? []}
                  changeLineItems={updateLineItems}
                  index={index ?? 0}
                  opened={index ?? 0}
                  setOpened={() => {
                    console.log('setOpened');
                  }}
                />
              </WBBox>
            )}
            <WBTypography color={'error'} fontSize={'12px'}>
              {getNestedError(errors, field.name)?.message as string}
            </WBTypography>
          </>
        )}
      />
      <ScanFeedback
        analysisId={bill?.documentAnalysis?.id}
        initialValue={(bill as any)?.documentAnalysis?.isCorrect ?? null}
      />
    </>
  );
};

const LineItemsContainer = styled(WBFlex)(({ theme }) => ({
  marginTop: theme.spacing(4),
  flexDirection: 'column',
  width: '100%',
}));
